import type { Action } from '../common/actions'

import type { SideModuleContentType } from './actions'
import {
    AutoNavigateSet,
    CarouselCastingStarted,
    CarouselCastingStopped,
    CarouselVideoPlayStatusChanged,
    CarouselViewEntered,
    CarouselViewSideModuleClosed,
    CarouselViewSideModuleOpened,
    CastSessionWasResumed,
    ChangedCastedFile,
    ChangedCurrentCarouselFile,
    FullscreenModeEntered,
    FullscreenModeExited,
    PlaybackOrderSet,
} from './actions'

export type VideoPlayStatus = 'playing' | 'paused' | 'finished'
export type PlaybackOrder = 'forward' | 'backward'
export type CastingDetail = { deviceName: string }
export type CarouselViewInfo = { jobID: JobID; fileID: FileID }
export type CarouselViewerState = {
    currentView?: CarouselViewInfo
    prevView?: CarouselViewInfo
    currentSideModule: SideModuleContentType | undefined
    isEmailFormOpen: boolean
    isInFullscreenMode: boolean
    shouldAutoNavigate: boolean
    playbackOrder?: PlaybackOrder
    videoPlayStatus?: VideoPlayStatus
    castingDetail?: CastingDetail // add more as needed
}

const initialState = {
    currentSideModule: undefined,
    isInFullscreenMode: false,
    shouldAutoNavigate: false,
    isEmailFormOpen: false,
}

export function carouselViewerReducer(
    state: CarouselViewerState = initialState,
    action: Action,
): CarouselViewerState {
    if (CarouselViewEntered.match(action)) {
        const resetSideModule =
            action.payload.sourcePage === 'TimelinePage' && state.currentSideModule === 'comments'
        return {
            ...state,
            ...(resetSideModule ? { currentSideModule: undefined } : {}),
            currentView: action.payload,
            prevView: undefined,
        }
    }

    if (ChangedCurrentCarouselFile.match(action) || ChangedCastedFile.match(action)) {
        return {
            ...state,
            currentView: action.payload,
            prevView: state.currentView,
            videoPlayStatus: undefined,
            shouldAutoNavigate:
                state.currentView && state.currentView.jobID !== action.payload.jobID
                    ? false
                    : state.shouldAutoNavigate,
        }
    }

    if (CarouselViewSideModuleOpened.match(action)) {
        return {
            ...state,
            currentSideModule: action.payload,
        }
    }
    if (CarouselViewSideModuleClosed.match(action)) {
        return {
            ...state,
            currentSideModule: undefined,
        }
    }

    if (FullscreenModeEntered.match(action)) {
        return {
            ...state,
            isInFullscreenMode: true,
        }
    }

    if (FullscreenModeExited.match(action)) {
        return {
            ...state,
            isInFullscreenMode: false,
        }
    }

    if (CarouselCastingStarted.match(action)) {
        return {
            ...state,
            castingDetail: {
                deviceName: action.payload.castDeviceName,
            },
        }
    }

    if (CarouselCastingStopped.match(action)) {
        return {
            ...state,
            castingDetail: undefined,
        }
    }
    if (AutoNavigateSet.match(action)) {
        return {
            ...state,
            shouldAutoNavigate: action.payload,
        }
    }
    if (PlaybackOrderSet.match(action)) {
        return {
            ...state,
            playbackOrder: action.payload,
        }
    }
    if (CarouselVideoPlayStatusChanged.match(action)) {
        return {
            ...state,
            videoPlayStatus: action.payload,
        }
    }

    if (CastSessionWasResumed.match(action)) {
        return {
            ...state,
            currentView: {
                jobID: action.payload.castedItem.jobID,
                fileID: action.payload.castedItem.fileID,
            },
        }
    }

    return state
}

export const carouselViewerReducerMapObj = {
    carouselViewer: carouselViewerReducer,
}

export type StateWithCarouselViewer = StateOfReducerMapObj<typeof carouselViewerReducerMapObj>
